import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Save, Check } from "lucide-react";
import ImageUploader from "../ImageUploader";
import BilingualField from "../BilingualField";

const SECTIONS = [
  { id: "hero", label: "Hero (Accueil)", fields: ["overline:bi", "title:bi-ta", "subtitle:bi-ta", "cta_label:bi", "image_url:img"] },
  { id: "services_intro", label: "Intro Services", fields: ["overline:bi", "title:bi", "description:bi-ta"] },
  { id: "about", label: "À propos", fields: ["overline:bi", "title:bi", "description:bi-ta", "long_description:bi-ta", "image_url:img"] },
  { id: "features", label: "Points forts", fields: ["overline:bi", "title:bi", "items:features"] },
  { id: "testimonials_intro", label: "Intro Témoignages", fields: ["overline:bi", "title:bi", "description:bi-ta"] },
  { id: "cta", label: "Bloc CTA", fields: ["overline:bi", "title:bi", "description:bi-ta", "cta_label:bi"] },
  { id: "stats", label: "Statistiques", fields: ["items:stats"] },
  { id: "contact_info", label: "Coordonnées", fields: ["title:bi", "description:bi-ta", "email:t", "phone:t", "location:bi", "instagram:t"] },
  { id: "footer", label: "Pied de page", fields: ["brand:t", "tagline:bi", "copyright:bi"] },
  { id: "whatsapp", label: "Bouton WhatsApp", fields: ["enabled:bool", "phone:t-help-intl", "message:bi-ta"] },
];

export default function ContentEditor() {
  const [section, setSection] = useState("hero");
  const [data, setData] = useState({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSaved(false);
    api.get(`/content/${section}`).then(({ data }) => setData(data)).catch(() => setData({}));
  }, [section]);

  const updateField = (key, value) => setData({ ...data, [key]: value });

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/content/${section}`, { data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally {
      setLoading(false);
    }
  };

  const current = SECTIONS.find((s) => s.id === section);

  return (
    <div data-testid="content-editor">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl">Contenu des pages</h1>
          <p className="text-[#4A3F35] mt-2">Textes et images du site. Chaque champ a une version <strong>FR</strong> et <strong>EN</strong>.</p>
        </div>
        <button onClick={save} disabled={loading} className="btn-primary disabled:opacity-50" data-testid="content-save">
          {saved ? <><Check size={16} /> Enregistré</> : <><Save size={16} /> Enregistrer</>}
        </button>
      </div>

      <div className="bg-[#FDFBF7] rounded-2xl p-2 mb-6 flex flex-wrap gap-1 border border-[#2D2A26]/10">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} className={`px-4 py-2 rounded-xl text-sm transition ${section === s.id ? "bg-[#2D2A26] text-white" : "hover:bg-[#F6F3EC]"}`} data-testid={`section-tab-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="bg-[#FDFBF7] rounded-2xl p-8 border border-[#2D2A26]/10 space-y-5">
        {current?.fields.map((spec) => {
          const [key, kind] = spec.split(":");
          return <FieldRenderer key={key} fieldKey={key} kind={kind} value={data[key]} onChange={(v) => updateField(key, v)} />;
        })}
      </div>
    </div>
  );
}

function FieldRenderer({ fieldKey, kind, value, onChange }) {
  const label = labelize(fieldKey);
  if (kind === "img") {
    return <ImageUploader value={value} onChange={onChange} label={label} testid={`field-${fieldKey}`} />;
  }
  if (kind === "bi") {
    return <BilingualField label={label} value={value} onChange={onChange} testid={`field-${fieldKey}`} />;
  }
  if (kind === "bi-ta") {
    return <BilingualField label={label} value={value} onChange={onChange} type="textarea" rows={fieldKey === "long_description" ? 6 : 3} testid={`field-${fieldKey}`} />;
  }
  if (kind === "stats" || kind === "features") {
    return <ItemsArrayEditor kind={kind} value={value || []} onChange={onChange} />;
  }
  if (kind === "bool") {
    return (
      <div className="flex items-center justify-between gap-4 border border-[#2D2A26]/15 rounded-xl px-4 py-3">
        <div>
          <label className="overline block">{label}</label>
          <p className="text-xs text-[#4A3F35]/70 mt-0.5">Activer/désactiver l&apos;affichage public.</p>
        </div>
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${value ? "bg-[#C87A5E]" : "bg-[#2D2A26]/20"}`}
          data-testid={`field-${fieldKey}`}
          aria-pressed={!!value}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${value ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    );
  }
  if (kind === "t-help-intl") {
    return (
      <div>
        <label className="overline block mb-2">{label}</label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="+33612345678" className="w-full border border-[#2D2A26]/15 rounded-xl px-4 py-2.5 text-sm focus:border-[#C87A5E] outline-none" data-testid={`field-${fieldKey}`} />
        <p className="text-xs text-[#4A3F35]/70 mt-1.5">Format international avec indicatif pays (ex : <code>+33612345678</code>). Sans espaces ni tirets.</p>
      </div>
    );
  }
  // plain text
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-[#2D2A26]/15 rounded-xl px-4 py-2.5 text-sm focus:border-[#C87A5E] outline-none" data-testid={`field-${fieldKey}`} />
    </div>
  );
}

function ItemsArrayEditor({ kind, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const isStats = kind === "stats";

  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const makeId = () => `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const add = () => onChange([...items, isStats ? { _key: makeId(), value: "0", suffix: "+", label: { fr: "", en: "" } } : { _key: makeId(), title: { fr: "", en: "" }, description: { fr: "", en: "" } }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="overline block mb-3">Éléments</label>
      <div className="space-y-4">
        {items.map((it, i) => {
          const itemKey = it._key || `${isStats ? it.value : (it.title?.fr || it.title?.en || "")}-${i}`;
          return (
          <div key={itemKey} className="border border-[#2D2A26]/15 rounded-xl p-4 space-y-3" data-testid={`item-${i}`}>
            {isStats ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Input small label="value" value={it.value} onChange={(v) => update(i, "value", v)} />
                  <Input small label="suffix" value={it.suffix} onChange={(v) => update(i, "suffix", v)} />
                </div>
                <BilingualField label="label" value={it.label} onChange={(v) => update(i, "label", v)} testid={`item-${i}-label`} />
              </>
            ) : (
              <>
                <BilingualField label="title" value={it.title} onChange={(v) => update(i, "title", v)} testid={`item-${i}-title`} />
                <BilingualField label="description" value={it.description} onChange={(v) => update(i, "description", v)} type="textarea" testid={`item-${i}-desc`} />
              </>
            )}
            <button onClick={() => remove(i)} className="text-xs text-red-700">Supprimer</button>
          </div>
          );
        })}
      </div>
      <button onClick={add} className="btn-ghost mt-4 !py-2 !px-4 text-xs">+ Ajouter</button>
    </div>
  );
}

function Input({ label, value, onChange, small }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`w-full border border-[#2D2A26]/15 rounded-xl px-3 ${small ? "py-2" : "py-2.5"} text-sm focus:border-[#C87A5E] outline-none`} />
    </div>
  );
}

function labelize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
